import React, { useMemo } from 'react';
import { BezierEdge, EdgeProps } from 'reactflow';
import {
  CanvasElementState,
  EDGE_INTERACTION_WIDTH,
} from '@components/MainStage/canvasStates';
import { useIsEdgeHovered } from '@components/MainStage/hover.store';
import { CanvasTheme } from '@constants/canvas/canvasTheme';

const AMBIENT_STROKE: Record<CanvasElementState, string> = {
  [CanvasElementState.Default]: CanvasTheme.line.default.stroke,
  [CanvasElementState.Faded]: CanvasTheme.line.faded.stroke,
  [CanvasElementState.Disabled]: CanvasTheme.line.disabled.stroke,
};

const AMBIENT_WIDTH: Record<CanvasElementState, number> = {
  [CanvasElementState.Default]: CanvasTheme.line.default.width,
  [CanvasElementState.Faded]: CanvasTheme.line.faded.width,
  [CanvasElementState.Disabled]: CanvasTheme.line.disabled.width,
};

export function CustomEdge({ id, selected, data, ...props }: EdgeProps) {
  const state: CanvasElementState =
    data?.canvas?.state ?? CanvasElementState.Default;
  const isHovered = useIsEdgeHovered(id);

  const isDisabled = state === CanvasElementState.Disabled;
  const isFocused = !isDisabled && (Boolean(selected) || isHovered);

  const style = useMemo(() => {
    if (isFocused) {
      return {
        stroke: CanvasTheme.line.focused.stroke,
        strokeWidth: CanvasTheme.line.focused.width,
        ...(selected ? { strokeDasharray: CanvasTheme.line.selectedDash } : {}),
      };
    }

    return {
      stroke: AMBIENT_STROKE[state],
      strokeWidth: AMBIENT_WIDTH[state],
      ...(isDisabled ? { pointerEvents: 'none' as const } : {}),
    };
  }, [isFocused, selected, state, isDisabled]);

  return (
    <BezierEdge
      {...props}
      id={id}
      // A Disabled line is non-interactive, so it gets no hit path.
      interactionWidth={isDisabled ? 0 : EDGE_INTERACTION_WIDTH}
      style={style}
    />
  );
}
